import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { createAxiosInstance, responseInterceptor } from './baseAxiosInstance'

import { getWbeUrl } from '@src/state/site/selectors'
import site from '@src/state/site/site'
import { captureError } from '../errors'
import { tokenRequest } from './cfeAuthApi'

const wbeBaseUrl = getWbeUrl(site)

const wbeDataApi = createAxiosInstance(wbeBaseUrl)

export interface RequestWithRetry extends AxiosRequestConfig {
  retry?: boolean
}

// Token request management strategy from
//  https://github.com/axios/axios/issues/450#issuecomment-247446276
let awaitingTokenRequest: null | Promise<any> = null

export const awaitToken = () => {
  if (!awaitingTokenRequest) {
    awaitingTokenRequest = tokenRequest().then(resetAwaitingToken, error => {
      resetAwaitingToken()
      throw error
    })
  }
  return awaitingTokenRequest
}

const resetAwaitingToken = () => (awaitingTokenRequest = null)

export const errorInterceptor = (api: AxiosInstance) => async (error: AxiosError) => {
  const originalRequest = error.config
  if (isRetry(originalRequest) || !checkStatusCode(error)) {
    return Promise.reject(error)
  }

  // See above ^^, token request only attempted on 401 or 403
  try {
    await awaitToken()
    const retryConfig = {
      ...originalRequest,
      retry: true, // <- `retry` property added to config
    }
    return api(retryConfig)
  } catch (authError) {
    const {
      config: { method, url },
      // @ts-ignore
      response: { status },
    } = error
    const originalRequestSignature = `${method &&
      method.toUpperCase()} ${url} -> ${status}`

    // // TODO -> confirm with team expected handling of cascading errors.
    // //  Currently swallow the cfeAuth error
    captureError(
      new Error(`CFE auth token failure, status ${authError.response.status}. 
    Dispatched in response to failed request ${originalRequestSignature}`)
    )

    return Promise.reject(error) // return the original error
  }
}

wbeDataApi.interceptors.response.use(responseInterceptor, errorInterceptor(wbeDataApi))

export const checkStatusCode = (error: AxiosError): boolean => {
  if (!error.response) {
    return false
  }
  const { status } = error.response
  return status === 401 || status === 403
}

export const isRetry = (request: RequestWithRetry): boolean =>
  !!(request && request.retry)

export default wbeDataApi



// WEBPACK FOOTER //
// ./src/utils/http-clients/wbeDataApi.ts